import * as yup from 'yup';

/**
 * E45 · Sleep diary — the both-ends input codecs of the three sleep mutations (plan 03 §2.1 #4): the phone / the hub (before they send)
 * and — as the shield's inputRule — the API run the SAME schema. SHAPE only: the order of the times, the limits on how far back a
 * night may be entered, the window floors and the relation with the client are the deciders' and the resolver's (SLEEP_* codes, the
 * board's coded refusals in the person's language), so the wire answers with the board's code instead of a generic validation error.
 *
 * The schemas are typed `yup.AnyObjectSchema` on purpose (TS7056 — see healthData.ts); the wire types come from the GraphQL codegen.
 */
const day = yup
  .string()
  .trim()
  .matches(/^\d{4}-\d{2}-\d{2}$/)
  .required();

const clock = yup
  .string()
  .trim()
  .matches(/^([01]\d|2[0-3]):[0-5]\d$/)
  .required();

const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const minutes = yup
  .number()
  .integer()
  .min(0)
  .max(24 * 60);

export const SLEEP_NIGHT_KIND_VALUES = ['slept', 'noSleep', 'skipped'] as const;
export const SLEEP_SOURCE_VALUES = ['device', 'manual'] as const;
export const SLEEP_PROTOCOL_VALUES = ['cbti', 'bbti'] as const;

const nap = yup.object({
  start: yup
    .string()
    .trim()
    .matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)
    .required(),
  minutes: yup.number().integer().min(1).max(600).required(),
});

export const confirmSleepNightInputSchema: yup.AnyObjectSchema = yup.object({
  subject: userRef.nullable(),
  night: day,
  today: yup
    .string()
    .trim()
    .matches(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  timeZone: yup.string().trim().max(64).nullable(),
  source: yup
    .string()
    .oneOf([...SLEEP_SOURCE_VALUES])
    .nullable(),
  nightKind: yup
    .string()
    .oneOf([...SLEEP_NIGHT_KIND_VALUES])
    .nullable(),
  bedTime: yup.date().nullable(),
  lightsOut: yup.date().nullable(),
  sleepOnsetMinutes: minutes.nullable(),
  awakenings: yup.number().integer().min(0).max(100).nullable(),
  wakeMinutes: minutes.nullable(),
  finalWake: yup.date().nullable(),
  outOfBed: yup.date().nullable(),
  quality: yup.number().integer().min(1).max(5).nullable(),
  naps: yup.array().of(nap.required()).max(10).nullable(),
  substances: yup
    .object({
      caffeineAfterNoon: yup.number().integer().min(0).max(50).nullable(),
      alcoholDrinks: yup.number().integer().min(0).max(50).nullable(),
      sleepAid: yup.string().trim().max(120).nullable(),
    })
    .nullable()
    .default(null),
  note: yup.string().max(2000).nullable(),
});

export const setSleepWindowInputSchema: yup.AnyObjectSchema = yup.object({
  subject: userRef.required(),
  bedtime: clock,
  riseTime: clock,
  effectiveFrom: day,
  protocol: yup
    .string()
    .oneOf([...SLEEP_PROTOCOL_VALUES])
    .nullable(),
  basis: yup
    .object({
      nights: yup.number().integer().min(0).max(31).required(),
      meanTimeInBed: yup.string().trim().max(16).required(),
      meanTotalSleep: yup.string().trim().max(16).required(),
      meanEfficiency: yup.number().integer().min(0).max(100).required(),
    })
    .nullable()
    .default(null),
  note: yup.string().max(1000).nullable(),
});

export const clearSleepWindowInputSchema: yup.AnyObjectSchema = yup.object({
  subject: userRef.required(),
  windowId: yup
    .string()
    .trim()
    .matches(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    )
    .required(),
  reason: yup.string().max(1000).nullable(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const confirmSleepNightArgsSchema: yup.AnyObjectSchema = yup.object({
  input: confirmSleepNightInputSchema.required(),
});
export const setSleepWindowArgsSchema: yup.AnyObjectSchema = yup.object({
  input: setSleepWindowInputSchema.required(),
});
export const clearSleepWindowArgsSchema: yup.AnyObjectSchema = yup.object({
  input: clearSleepWindowInputSchema.required(),
});

export const sleepValidators = {
  confirmSleepNightInputSchema,
  setSleepWindowInputSchema,
  clearSleepWindowInputSchema,
  confirmSleepNightArgsSchema,
  setSleepWindowArgsSchema,
  clearSleepWindowArgsSchema,
};
